import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FiCheckCircle, FiLoader, FiAlertTriangle } from 'react-icons/fi';
import { toast } from '../utils/customToast';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';

const PaymentSuccess = () => {
    usePageTitle('Paiement confirmé');
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { getAuthHeaders, activeTenantId, refreshUser } = useAuth();
    const [status, setStatus] = useState('processing'); // processing, success, error
    const [message, setMessage] = useState('Traitement de votre paiement...');
    
    console.log('PaymentSuccess component loaded');

    useEffect(() => {
        const processPayment = async () => {
            const sessionId = searchParams.get('session_id');
            
            console.log('Processing payment with sessionId:', sessionId);
            

            if (!sessionId) {
                setStatus('error');
                setMessage('Paiement annulé. Vous n\'avez pas été débité.');
                toast.error('Paiement annulé', {
                    position: "top-right",
                    autoClose: 5000,
                });
                return;
            }

            // Le webhook Stripe s'occupera de mettre à jour le plan automatiquement
            // On affiche juste un message de succès
            setStatus('success');
            setMessage('Paiement confirmé! Votre plan sera mis à jour sous peu.');
            
            // Notification de succès
            toast.success('Paiement confirmé avec succès!', {
                position: "top-right",
                autoClose: 5000,
            });

            // Actualiser les données utilisateur après un délai pour laisser le temps au webhook
            setTimeout(async () => {
                if (typeof refreshUser === 'function') {
                    await refreshUser();
                }
                navigate('/messages');
            }, 3000);
        };

        processPayment();
    }, [searchParams, navigate, getAuthHeaders, activeTenantId, refreshUser]);

    const getIcon = () => {
        switch (status) {
            case 'processing':
                return <FiLoader className="w-16 h-16 text-blue-500 animate-spin" aria-hidden="true" />;
            case 'success':
                return <FiCheckCircle className="w-16 h-16 text-green-500" aria-hidden="true" />;
            case 'error':
                return <FiAlertTriangle className="w-16 h-16 text-red-500" aria-hidden="true" />;
            default:
                return null;
        }
    };

    const getStatusColor = () => {
        switch (status) {
            case 'processing':
                return 'text-blue-600';
            case 'success':
                return 'text-green-600';
            case 'error':
                return 'text-red-600';
            default:
                return 'text-gray-600';
        }
    };

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center px-4">
            <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
                <div className="flex justify-center mb-6">
                    {getIcon()}
                </div>
                
                <h1 className={`text-2xl font-bold mb-4 ${getStatusColor()} dark:text-white`}>
                    {status === 'processing' && 'Traitement en cours...'}
                    {status === 'success' && 'Paiement réussi !'}
                    {status === 'error' && 'Attention'}
                </h1>
                
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                    {message}
                </p>
                
                {status === 'success' && (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4 mb-6">
                        <p className="text-green-800 dark:text-green-300 text-sm">
                            Vous allez être redirigé vers votre tableau de bord dans quelques secondes...
                        </p>
                    </div>
                )}
                
                {status === 'error' && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4 mb-6">
                        <p className="text-red-800 dark:text-red-300 text-sm">
                            Votre paiement a été confirmé par Stripe, mais il y a eu un problème technique. 
                            Contactez le support avec votre ID de session.
                        </p>
                    </div>
                )}
                
                <button
                    onClick={() => navigate('/messages')}
                    className="w-full bg-primary hover:bg-primary-hover text-white dark:text-black font-medium py-2 px-4 rounded-md transition-colors"
                >
                    Retourner au tableau de bord
                </button>
            </div>
        </div>
    );
};

export default PaymentSuccess;